import { useCallback, useEffect, useState, type ReactNode } from "react";
import { AccountingSyncBadge, InvoiceAccountingSyncCard } from "@/components/AccountingSync";
import { toast } from "sonner";
import { useDolfinClient, useDolfinAuth } from "@/components/DolfinProvider";
import { usePermissions } from "@/lib/permissions";
import { apiErrorMessage } from "@/lib/api-error";
import type { components } from "@dolfin/api-admin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Send, Download, Pencil, Trash2, Plus, Receipt, LockIcon, Mail, ExternalLink, FileCheck2 } from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { CopyButton } from "@/components/ui/copy-button";
import { EInvoiceDelivery } from "@/components/EInvoiceDelivery";
import { InvoiceWhatsAppMessages } from "@/components/InvoiceWhatsAppMessages";
import { SendChannelChoices, SendPlanSummary, networkLabel, type SendFix, type SendPlan } from "@/components/SendPlanSummary";
import { WhatsAppIcon } from "@/components/icons/WhatsAppIcon";
import { EditCustomerDialog } from "@/components/EditCustomerDialog";
import { CreditNoteEInvoiceStatus } from "@/components/CreditNoteEInvoiceStatus";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ResizablePanelGroup,
  ResizablePanel,
  ResizableHandle,
} from "@/components/ui/resizable";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "@/components/ui/tabs";

import { daysFromToday, formatCalendarDate, formatCurrency, formatDate, formatDateTime } from "@/lib/format";
import { useBankDetailsConfigured } from "@/hooks/useBankDetailsConfigured";
import { OrganisationSettingsDialog } from "@/components/OrganisationSettingsDialog";
import {
  ReminderOffsetMultiSelect,
  reminderOffsetsEqual,
} from "@/components/ReminderOffsetMultiSelect";
import { PdfFrame } from "@/components/PdfFrame";
import { cn } from "@/lib/utils";
import { InfoHint } from "@/components/InfoHint";
import { useScrollToHash } from "@/hooks/useScrollToHash";
import { InvoiceStatusPill } from "@/components/InvoicesListPage";
import {
  ErrorState,
  HeaderRow,
  LoadingState,
  TH,
  TableShell,
  Timeline,
  type TimelineEntry,
} from "@/components/PageUi";

type InvoiceResponse = components["schemas"]["InvoiceResponse"];
type InvoicePaymentResponse = components["schemas"]["InvoicePaymentResponse"];
type CreditNoteResponse = components["schemas"]["CreditNoteResponse"];
type TaxRateResponse = components["schemas"]["TaxRateResponse"];
type InvoiceEmailPreview = components["schemas"]["InvoiceEmailPreviewResponse"];
type InvoiceTone = components["schemas"]["InvoiceTone"];
type InvoiceReminderOffset = components["schemas"]["InvoiceReminderOffset"];
type OrganisationResponse = components["schemas"]["OrganisationResponse"];
type OrganisationArSettingsResponse = components["schemas"]["OrganisationArSettingsResponse"];

const TONE_OPTIONS: { value: InvoiceTone; label: string; description: string }[] = [
  { value: "Friendly", label: "Friendly", description: "Warm and conversational." },
  { value: "Direct", label: "Direct", description: "Brief and to the point." },
  { value: "Formal", label: "Formal", description: "Reserved and businesslike." },
];

const PAYMENT_RECORDABLE_STATUSES = new Set(["Issued", "PartiallyPaid"]);
// Credit notes can only be raised against an invoice that's been sent (and not yet settled).
const CREDIT_NOTE_CREATABLE_STATUSES = new Set(["Issued", "PartiallyPaid"]);

function todayIso() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

type InvoiceAuditLogResponse = components["schemas"]["InvoiceAuditLogResponse"];

// The audit trail's event names are the API's vocabulary. These are the words a person reads.
const INVOICE_EVENT_LABELS: Record<string, string> = {
  InvoiceCreated: "Draft created",
  InvoiceFieldsUpdated: "Details edited",
  InvoiceIssued: "Invoice issued",
  InvoiceSent: "Sent to the customer",
  InvoicePaid: "Payment received",
  InvoiceVoided: "Invoice voided",
  InvoiceReminderSent: "Reminder sent",
  InvoiceReminderFailed: "Reminder could not be sent",
  CreditNoteIssued: "Credit note applied",
};

const INVOICE_STATE_WORDS: Record<string, string> = {
  PartiallyPaid: "partially paid",
};

function stateWord(state?: string | null) {
  return state ? (INVOICE_STATE_WORDS[state] ?? state.toLowerCase()) : null;
}

function historyEntry(entry: InvoiceAuditLogResponse, i: number): TimelineEntry {
  const to = stateWord(entry.toState);
  const title =
    entry.action === "InvoiceStateChanged"
      ? to
        ? `Marked ${to}`
        : "Status changed"
      : ((entry.action ? INVOICE_EVENT_LABELS[entry.action] : undefined) ?? "Updated");
  return {
    id: entry.id ?? String(i),
    title,
    at: entry.createdAt,
    detail: entry.actorType === "System" ? "Automatic" : null,
  };
}

export interface InvoicePageProps {
  invoiceId: string;
  onBack?: () => void;
  /** Called after a successful delete. Falls back to onBack if omitted. */
  onDeleted?: () => void;
  onEditInvoice: (id: string) => void;
  onCustomerClick: (id: string) => void;
  onProductClick: (id: string) => void;
  onCreditNoteClick?: (creditNoteId: string) => void;
  /** Without it the E-send section is absent rather than disabled: the feature isn't the client's to use. */
  eInvoicingEnabled?: boolean;
  /** Null while unknown. The send dialog reads this from the send plan; the direct sends need it passed in. */
  eInvoicingRegistered?: boolean | null;
  /** Without it, a setup problem stopping the send is shown without a button. */
  onEInvoicingSettingsClick?: () => void;
  /** The SignalR host, for live delivery updates. Without it the delivery card polls instead. */
  hubBaseUrl?: string;
  /** The client holds ar.accounting and the organisation has connected its platform. */
  accountingEnabled?: boolean;
  /** The host's Books card (S6), when the client holds `books`. */
  booksCard?: ReactNode;
  /** Whether the client holds the WhatsApp module. Shows the invoice's WhatsApp messages beside its history. */
  whatsAppEnabled?: boolean;
  /** When true, auto-opens the send modal (e.g. arriving from the list "Send" action). */
  autoOpenSendModal?: boolean;
  /** Called once the send modal has been auto-opened, so the host can clear its trigger. */
  onSendModalAutoOpened?: () => void;
}

export function InvoicePage({
  invoiceId,
  onBack,
  onDeleted,
  onEditInvoice,
  onCustomerClick,
  onCreditNoteClick,
  onProductClick,
  eInvoicingEnabled,
  eInvoicingRegistered,
  onEInvoicingSettingsClick,
  hubBaseUrl,
  accountingEnabled,
  booksCard,
  whatsAppEnabled,
  autoOpenSendModal,
  onSendModalAutoOpened,
}: InvoicePageProps) {
  const api = useDolfinClient();
  const { authFetch, organisationId } = useDolfinAuth();
  const { can, roleAllows, demoReason } = usePermissions();
  const canSendChase = can("sendChase");
  // Issuing sends nothing, so a demo may issue even though it can't send.
  const canIssue = roleAllows("sendChase");
  const sendDemoReason = demoReason("sendChase");
  const [invoice, setInvoice] = useState<InvoiceResponse | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  // Where the send will go and what stops it (GET /send-plan), loaded each time the dialog opens.
  const [sendPlan, setSendPlan] = useState<SendPlan | null>(null);
  const [sendPlanLoading, setSendPlanLoading] = useState(false);
  const [sendPlanError, setSendPlanError] = useState<string | null>(null);
  // Reset each time the dialog opens; the plan is re-read per choice, so the dialog shows exactly what the send will do.
  const [eInvoiceChoice, setEInvoiceChoice] = useState<components["schemas"]["InvoiceEInvoiceChoice"]>("Auto");
  const [emailChoice, setEmailChoice] = useState<components["schemas"]["InvoiceEmailChoice"]>("AfterNetwork");
  const [fixCustomer, setFixCustomer] = useState<components["schemas"]["CustomerResponse"] | null>(null);
  const [fixCustomerOpen, setFixCustomerOpen] = useState(false);
  const [deletingInvoice, setDeletingInvoice] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [payments, setPayments] = useState<InvoicePaymentResponse[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [recordOpen, setRecordOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentPaidAt, setPaymentPaidAt] = useState<string | null>(todayIso());
  const [paymentNotes, setPaymentNotes] = useState("");
  const [paymentReference, setPaymentReference] = useState("");
  // Books links a matched bank transaction here at #payments.
  useScrollToHash("payments", !loading && invoice !== null);
  const [history, setHistory] = useState<InvoiceAuditLogResponse[]>([]);
  const [creditNotes, setCreditNotes] = useState<CreditNoteResponse[]>([]);
  const [creditNotesLoading, setCreditNotesLoading] = useState(false);
  const [cnDialogOpen, setCnDialogOpen] = useState(false);
  const [cnCreating, setCnCreating] = useState(false);
  const [cnAmount, setCnAmount] = useState("");
  const [cnReason, setCnReason] = useState("");
  // "" → credit the whole invoice. Otherwise the credit is tied to a specific line item, whose
  // frozen tax rate is inherited. For a whole-invoice credit the user picks a tax rate explicitly.
  const [cnLineItemId, setCnLineItemId] = useState("");
  const [cnTaxRateId, setCnTaxRateId] = useState("");
  const [taxRates, setTaxRates] = useState<TaxRateResponse[]>([]);
  // Per-row in-flight ids so only the acted-on credit note shows a spinner.
  const [issuingCnId, setIssuingCnId] = useState<string | null>(null);
  const [deletingCnId, setDeletingCnId] = useState<string | null>(null);
  const { configured: bankDetailsConfigured, refresh: refreshBankDetails } = useBankDetailsConfigured();
  const [previewTab, setPreviewTab] = useState<"pdf" | "email" | "pay">("pdf");
  const [emailPreview, setEmailPreview] = useState<InvoiceEmailPreview | null>(null);
  const [emailPreviewLoading, setEmailPreviewLoading] = useState(false);
  const [emailPreviewError, setEmailPreviewError] = useState<string | null>(null);
  // Client-level AR-email kill switch: when off, sending still marks the invoice Sent but no email goes, so the
  // preview is hidden. Defaults true until known.
  const [arEmailsEnabled, setArEmailsEnabled] = useState(true);

  // Dirty flags send only the fields the user edited, so the audit (OpeningBodyWasOverridden) stays meaningful.
  const [organisation, setOrganisation] = useState<OrganisationResponse | null>(null);
  // Invoice tone and reminder defaults live on the AR settings.
  const [arSettings, setArSettings] = useState<OrganisationArSettingsResponse | null>(null);
  const [modalTone, setModalTone] = useState<InvoiceTone>("Friendly");
  const [modalSubject, setModalSubject] = useState("");
  const [modalGreetingLine, setModalGreetingLine] = useState("");
  const [modalOpeningBody, setModalOpeningBody] = useState("");
  const [modalPreviewHtml, setModalPreviewHtml] = useState<string | null>(null);
  const [modalSubjectDirty, setModalSubjectDirty] = useState(false);
  const [modalGreetingLineDirty, setModalGreetingLineDirty] = useState(false);
  const [modalOpeningBodyDirty, setModalOpeningBodyDirty] = useState(false);
  const [modalPreviewLoading, setModalPreviewLoading] = useState(false);
  const [modalPreviewError, setModalPreviewError] = useState<string | null>(null);
  // Attached as an override only if the user changed the selection (same dirty pattern).
  const [modalReminders, setModalReminders] = useState<InvoiceReminderOffset[]>([]);
  const [modalRemindersDirty, setModalRemindersDirty] = useState(false);
  // Sent as overrides only when changed (same dirty pattern).
  const [modalCc, setModalCc] = useState("");
  const [modalCcDirty, setModalCcDirty] = useState(false);
  const [modalReplyTo, setModalReplyTo] = useState("");
  const [modalReplyToDirty, setModalReplyToDirty] = useState(false);

  async function fetchPayments(id: string) {
    setPaymentsLoading(true);
    try {
      const { data, error: apiError } = await api.GET("/v1/invoices/{id}/payments", {
        params: { path: { id } },
      });
      if (apiError) {
        console.error("Failed to load payments:", apiError);
        return;
      }
      setPayments(data ?? []);
    } finally {
      setPaymentsLoading(false);
    }
  }

  async function refreshInvoice(id: string) {
    const { data } = await api.GET("/v1/invoices/{id}", {
      params: { path: { id } },
    });
    if (data) setInvoice(data);
  }

  async function fetchCreditNotes(id: string) {
    setCreditNotesLoading(true);
    try {
      const { data, error: apiError } = await api.GET(
        "/v1/invoices/{invoiceId}/credit-notes",
        { params: { path: { invoiceId: id } } },
      );
      if (apiError) {
        console.error("Failed to load credit notes:", apiError);
        return;
      }
      setCreditNotes(data ?? []);
    } finally {
      setCreditNotesLoading(false);
    }
  }

  function resetCnForm() {
    setCnAmount("");
    setCnReason("");
    setCnLineItemId("");
    setCnTaxRateId("");
  }

  // A whole-invoice credit with no rate picked takes the invoice's own rate when every line shares one; lines at
  // different rates leave nothing to default to, so the server asks for one and so does the form.
  const cnLineRates = invoice?.lineItems?.map((li) => li.taxRateSnapshot ?? null) ?? [];
  const cnDistinctRates = [
    ...new Map(cnLineRates.filter((r) => r !== null).map((r) => [`${r.sourceId}|${Number(r.rate)}`, r])).values(),
  ];
  const cnRatesDiffer = cnDistinctRates.length > 1 || (cnDistinctRates.length === 1 && cnLineRates.includes(null));
  const cnInvoiceRate = cnRatesDiffer ? null : (cnDistinctRates[0] ?? null);
  const cnNeedsRate = cnRatesDiffer && !cnLineItemId && !cnTaxRateId;

  async function handleCreateCreditNote() {
    if (!invoiceId || cnCreating) return;
    const amount = parseFloat(cnAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Enter a credit amount greater than zero.");
      return;
    }

    setCnCreating(true);
    try {
      const { error: apiError } = await api.POST(
        "/v1/invoices/{invoiceId}/credit-notes",
        {
          params: { path: { invoiceId } },
          body: {
            amount,
            reason: cnReason.trim() || null,
            invoiceLineItemId: cnLineItemId || null,
            // A line-item credit inherits that line's frozen rate (no taxRateId). A whole-invoice
            // credit applies the explicitly selected rate ("" → the invoice's rate, or zero-rated when it has none).
            taxRateId: cnLineItemId ? null : cnTaxRateId || null,
          },
        },
      );
      if (apiError) {
        toast.error(apiErrorMessage(apiError, "Failed to create credit note."));
        return;
      }
      toast.success("Credit note created");
      setCnDialogOpen(false);
      resetCnForm();
      await fetchCreditNotes(invoiceId);
    } finally {
      setCnCreating(false);
    }
  }

  // Loaded on first open of the credit-note dialog.
  useEffect(() => {
    if (!cnDialogOpen || taxRates.length > 0) return;
    let cancelled = false;
    (async () => {
      const { data } = await api.GET("/v1/tax-rates");
      if (!cancelled) setTaxRates(data?.data ?? []);
    })();
    return () => {
      cancelled = true;
    };
  }, [cnDialogOpen, taxRates.length, api]);

  // Issuing a credit note is the apply step: it reduces the invoice balance immediately and is
  // irreversible. If the credit clears the balance the invoice moves to Credited.
  async function handleIssueCreditNote(creditNoteId: string) {
    if (!invoiceId || issuingCnId) return;
    setIssuingCnId(creditNoteId);
    try {
      const { error: apiError } = await api.POST(
        "/v1/invoices/{invoiceId}/credit-notes/{creditNoteId}/issue",
        { params: { path: { invoiceId, creditNoteId } } },
      );
      if (apiError) {
        toast.error(apiErrorMessage(apiError, "Failed to issue credit note."));
        return;
      }
      toast.success("Credit note issued and applied");
      await Promise.all([fetchCreditNotes(invoiceId), refreshInvoice(invoiceId)]);
    } finally {
      setIssuingCnId(null);
    }
  }

  async function handleDeleteCreditNote(creditNoteId: string) {
    if (!invoiceId || deletingCnId) return;
    setDeletingCnId(creditNoteId);
    try {
      const { error: apiError } = await api.DELETE(
        "/v1/invoices/{invoiceId}/credit-notes/{creditNoteId}",
        { params: { path: { invoiceId, creditNoteId } } },
      );
      if (apiError) {
        toast.error(apiErrorMessage(apiError, "Failed to delete credit note."));
        return;
      }
      toast.success("Draft credit note deleted");
      await fetchCreditNotes(invoiceId);
    } finally {
      setDeletingCnId(null);
    }
  }

  useEffect(() => {
    if (!invoiceId) return;

    async function fetchInvoice() {
      setLoading(true);
      setError(null);

      try {
        const { data, error: apiError } = await api.GET("/v1/invoices/{id}", {
          params: { path: { id: invoiceId! } },
        });

        if (apiError || !data) {
          console.error("Failed to load invoice:", apiError);
          setError("Failed to load invoice");
          return;
        }

        setInvoice(data);
        fetchPayments(invoiceId!);
        fetchCreditNotes(invoiceId!);

        const pdfResponse = await authFetch(`/v1/invoices/${invoiceId}/pdf`);
        if (pdfResponse.ok) {
          const blob = await pdfResponse.blob();
          setPdfUrl(URL.createObjectURL(blob));
        }
      } catch (e) {
        console.error("Failed to load invoice:", e);
        setError("Failed to load invoice");
      } finally {
        setLoading(false);
      }
    }

    fetchInvoice();
  }, [invoiceId]);

  // History is a side panel, not part of the invoice: a failure here leaves the rest of the page
  // working and simply hides the card. Refetched when the status moves so a send or payment shows up.
  useEffect(() => {
    if (!invoiceId) return;
    let cancelled = false;
    api
      .GET("/v1/invoices/{invoiceId}/audit-logs", {
        params: { path: { invoiceId }, query: { Limit: 50 } },
      })
      .then(({ data }) => {
        if (!cancelled) setHistory(data?.data ?? []);
      });
    return () => {
      cancelled = true;
    };
  }, [api, invoiceId, invoice?.status, invoice?.amountPaid, invoice?.amountCredited]);

  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  // Notify the host so a refresh doesn't reopen it. Draft only: Send is disabled for any other state.
  useEffect(() => {
    if (!autoOpenSendModal) return;
    if (!invoice || invoice.status !== "Draft") return;
    setSendModalOpen(true);
    onSendModalAutoOpened?.();
  }, [autoOpenSendModal, invoice, onSendModalAutoOpened]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await api.GET("/v1/settings/ar", {});
      if (cancelled || !data) return;
      setArEmailsEnabled(data.arEmailsEnabled ?? true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Tone is sent as an override only when the user picks something other than the org default.
  useEffect(() => {
    if (!organisationId) return;
    let cancelled = false;
    (async () => {
      const [orgRes, arRes] = await Promise.all([
        api.GET("/v1/organisations/{id}", { params: { path: { id: organisationId } } }),
        api.GET("/v1/organisations/{organisationId}/settings/ar", {
          params: { path: { organisationId } },
        }),
      ]);
      if (cancelled || !orgRes.data) return;
      setOrganisation(orgRes.data);
      setArSettings(arRes.data ?? null);
      setModalTone(arRes.data?.defaultInvoiceMessageTone ?? "Friendly");
      // Untouched, `reminders` isn't attached and the backend uses the org default at send time.
      setModalReminders(arRes.data?.defaultReminderOffsets ?? []);
      setModalRemindersDirty(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [organisationId]);

  // The send plan: fetched when the dialog opens, and again after the user fixes a problem, so what the
  // dialog promises is what the send will do. The send refuses with the same problems.
  const loadSendPlan = useCallback(async () => {
    if (!invoiceId) return;
    setSendPlanLoading(true);
    setSendPlanError(null);
    try {
      const { data, error: apiError } = await api.GET("/v1/invoices/{id}/send-plan", {
        params: {
          path: { id: invoiceId },
          query: {
            eInvoice: eInvoiceChoice === "Auto" ? undefined : eInvoiceChoice,
            email: emailChoice === "AfterNetwork" ? undefined : emailChoice,
          },
        },
      });
      if (apiError || !data) {
        setSendPlanError(apiErrorMessage(apiError, "Couldn't check where this invoice goes."));
        return;
      }
      setSendPlan(data);
    } finally {
      setSendPlanLoading(false);
    }
  }, [api, invoiceId, eInvoiceChoice, emailChoice]);

  useEffect(() => {
    if (!sendModalOpen) return;
    void loadSendPlan();
  }, [sendModalOpen, loadSendPlan]);

  useEffect(() => {
    if (!sendModalOpen) return;
    setSendPlan(null);
    setEInvoiceChoice("Auto");
    setEmailChoice("AfterNetwork");
  }, [sendModalOpen]);

  // The email half of the dialog only exists when a PDF copy will actually be emailed: over a network
  // the copy is optional, and a customer without an address gets the e-invoice alone.
  const emailCopyPlanned = arEmailsEnabled && !!sendPlan?.email;

  // A Draft is issued by its send; an issued invoice still being chased can be sent later, or again.
  const sendable = invoice?.status === "Draft" || invoice?.status === "Issued" || invoice?.status === "PartiallyPaid";
  const alreadyDelivered = invoice?.status !== "Draft" && (!!invoice?.pdfCopyTo || !!invoice?.deliverAsEInvoice);
  const [issuing, setIssuing] = useState(false);
  const [issueConfirmOpen, setIssueConfirmOpen] = useState(false);

  // Draft → Issued without delivering anything: numbered, dated and locked, to be sent later or elsewhere. POST /issue
  // runs the send's checks itself (all but a missing email address) and refuses with the first problem.
  async function handleIssueOnly() {
    if (!invoiceId || issuing) return;
    setIssuing(true);
    try {
      const { data, error: apiError } = await api.POST("/v1/invoices/{id}/issue", { params: { path: { id: invoiceId } } });
      if (apiError || !data) {
        toast.error(apiErrorMessage(apiError, "Failed to issue invoice."));
        return;
      }
      setInvoice(data);
      setIssueConfirmOpen(false);
      toast.success("Invoice issued. Nothing was sent; send it when you're ready.");
      // Deliveries are recorded just after the issue (a demo's "Not sent - demo" too), so read it again to show them.
      for (const delay of [1500, 4000]) {
        window.setTimeout(async () => {
          const { data: fresh } = await api.GET("/v1/invoices/{id}", { params: { path: { id: invoiceId } } });
          if (fresh) setInvoice(fresh);
        }, delay);
      }
    } finally {
      setIssuing(false);
    }
  }

  async function handleFix(fix: SendFix) {
    if (fix === "Invoice") {
      setSendModalOpen(false);
      onEditInvoice(invoiceId);
      return;
    }
    if (fix === "Organisation") {
      setSendModalOpen(false);
      onEInvoicingSettingsClick?.();
      return;
    }
    // The customer is fixed in place, without leaving the send: the most common problem is their network
    // address, and the plan re-runs as soon as it is saved.
    if (!invoice?.customerId) return;
    const { data, error: apiError } = await api.GET("/v1/customers/{id}", {
      params: { path: { id: invoice.customerId } },
    });
    if (apiError || !data) {
      toast.error(apiErrorMessage(apiError, "Couldn't load the customer."));
      return;
    }
    setFixCustomer(data);
    setFixCustomerOpen(true);
  }

  // Each tone change replaces the form with the new catalogue cell (in-progress edits are overwritten) and
  // resets the dirty flags so the field-edit effect doesn't re-fetch them as overrides.
  useEffect(() => {
    if (!sendModalOpen || !invoiceId || !emailCopyPlanned) return;
    let cancelled = false;
    (async () => {
      setModalPreviewLoading(true);
      setModalPreviewError(null);
      try {
        const { data, error: apiError } = await api.GET("/v1/invoices/{id}/email-preview", {
          params: { path: { id: invoiceId }, query: { tone: modalTone } },
        });
        if (cancelled) return;
        if (apiError || !data) {
          setModalPreviewError(
            (apiError && (apiError.detail || apiError.title)) ||
              "Couldn't load the email preview.",
          );
          return;
        }
        setModalSubject(data.subject);
        setModalGreetingLine(data.greetingLine);
        setModalOpeningBody(data.openingBodyMarkdown);
        setModalPreviewHtml(data.htmlBody);
        setModalCc((data.cc ?? []).join(", "));
        setModalReplyTo(data.replyTo ?? "");
        setModalSubjectDirty(false);
        setModalGreetingLineDirty(false);
        setModalOpeningBodyDirty(false);
        setModalCcDirty(false);
        setModalReplyToDirty(false);
      } finally {
        if (!cancelled) setModalPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sendModalOpen, invoiceId, modalTone, emailCopyPlanned]);

  // Only once a field is dirty, so the tone effect can populate the form without a redundant fetch. Updates
  // only the HTML, so the response echo never clobbers edits.
  useEffect(() => {
    if (!sendModalOpen || !invoiceId || !emailCopyPlanned) return;
    if (!modalSubjectDirty && !modalGreetingLineDirty && !modalOpeningBodyDirty) return;
    let cancelled = false;
    const timeoutId = setTimeout(async () => {
      try {
        const { data, error: apiError } = await api.GET("/v1/invoices/{id}/email-preview", {
          params: {
            path: { id: invoiceId },
            query: {
              tone: modalTone,
              subject: modalSubjectDirty ? modalSubject : undefined,
              greetingLine: modalGreetingLineDirty ? modalGreetingLine : undefined,
              openingBodyMarkdown: modalOpeningBodyDirty ? modalOpeningBody : undefined,
            },
          },
        });
        if (cancelled || apiError || !data) return;
        setModalPreviewHtml(data.htmlBody);
      } catch {
        // Swallowed: mid-edit, a transient failure shouldn't clobber the preview; the next keystroke retries.
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [
    sendModalOpen,
    invoiceId,
    modalTone,
    modalSubject,
    modalGreetingLine,
    modalOpeningBody,
    modalSubjectDirty,
    modalGreetingLineDirty,
    modalOpeningBodyDirty,
    emailCopyPlanned,
  ]);

  // Lazy: the endpoint round-trips Postmark twice (template fetch + validate).
  useEffect(() => {
    if (previewTab !== "email" || !invoiceId || emailPreview || !arEmailsEnabled) return;

    let cancelled = false;
    (async () => {
      setEmailPreviewLoading(true);
      setEmailPreviewError(null);
      try {
        const { data, error: apiError } = await api.GET("/v1/invoices/{id}/email-preview", {
          params: { path: { id: invoiceId } },
        });
        if (cancelled) return;
        if (apiError || !data) {
          const message =
            (apiError && (apiError.detail || apiError.title)) ||
            "Failed to load email preview.";
          setEmailPreviewError(message);
          return;
        }
        setEmailPreview(data);
      } catch (e) {
        if (!cancelled) {
          setEmailPreviewError(e instanceof Error ? e.message : "Failed to load email preview.");
        }
      } finally {
        if (!cancelled) setEmailPreviewLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [previewTab, invoiceId, emailPreview, arEmailsEnabled]);

  // The Pay Page tab is hidden once an invoice is fully credited — move off it if the user was
  // watching it when the credit landed (e.g. issuing the credit that cleared the balance).
  useEffect(() => {
    if (invoice?.status === "Credited" && previewTab === "pay") {
      setPreviewTab("pdf");
    }
  }, [invoice?.status, previewTab]);

  async function handleSendInvoice() {
    if (!invoiceId || sending) return;
    setSending(true);
    try {
      // Only fields the user changed, so OpeningBodyWasOverridden stays accurate.
      const overrides: components["schemas"]["SendInvoiceOverrides"] = {};
      if (modalSubjectDirty) overrides.subject = modalSubject;
      if (modalGreetingLineDirty) overrides.greetingLine = modalGreetingLine;
      if (modalOpeningBodyDirty) overrides.openingBodyMarkdown = modalOpeningBody;
      if (arSettings && modalTone !== arSettings.defaultInvoiceMessageTone) {
        overrides.tone = modalTone;
      }
      // Reminders: omitted → org defaults; [] → none for this send; populated → exactly these.
      if (modalRemindersDirty) overrides.reminders = modalReminders;
      // CC / Reply-To: only when changed. CC splits to a list (blank → [] = no CC for this send);
      // Reply-To sends the trimmed address (blank → omit, backend falls back to the org default).
      if (modalCcDirty) {
        overrides.cc = modalCc.split(/[,\n]/).map((e) => e.trim()).filter((e) => e.length > 0);
      }
      if (modalReplyToDirty && modalReplyTo.trim()) overrides.replyTo = modalReplyTo.trim();
      if (eInvoiceChoice !== "Auto") overrides.eInvoice = eInvoiceChoice;
      if (emailChoice !== "AfterNetwork") overrides.email = emailChoice;
      const hasOverrides = Object.keys(overrides).length > 0;

      const { data, error: apiError } = await api.POST("/v1/invoices/{id}/send", {
        params: { path: { id: invoiceId } },
        body: hasOverrides ? overrides : null,
      });
      if (apiError || !data) {
        toast.error(apiErrorMessage(apiError, "Failed to send invoice."));
        return;
      }
      setInvoice(data);
      setSendModalOpen(false);
      if (sendPlan?.eInvoice && !sendPlan.eInvoice.status) {
        toast.success(
          `Sending the invoice over ${networkLabel(sendPlan.eInvoice.network)}` +
            (sendPlan.email ? ". The PDF copy follows once delivery is confirmed." : "."),
        );
      } else if (sendPlan?.email) {
        toast.success(`Emailing the invoice to ${sendPlan.email.to}.`);
      } else {
        toast.success("Invoice issued. Nothing was sent; share its PDF or payment link yourself.");
      }
      // Clear cached previews so reopening the modal (or the Preview Email tab) refetches
      // against the post-send invoice state.
      setEmailPreview(null);
      setModalPreviewHtml(null);
      // The Preview Email tab becomes disabled the moment the invoice leaves Draft, so move
      // the user to the Pay Page tab if that's where they were watching from.
      if (previewTab === "email") setPreviewTab("pay");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to send invoice.");
    } finally {
      setSending(false);
    }
  }

  function handleDownloadPdf() {
    if (!pdfUrl) return;
    const a = document.createElement("a");
    a.href = pdfUrl;
    a.download = `invoice-${invoice?.documentNumber || invoiceId}.pdf`;
    a.click();
  }

  async function handleRecordPayment() {
    if (!invoiceId || recording) return;
    const amount = parseFloat(paymentAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Enter a payment amount greater than zero.");
      return;
    }

    setRecording(true);
    try {
      const paidAtIso = paymentPaidAt ? `${paymentPaidAt}T00:00:00` : null;
      const { error: apiError } = await api.POST("/v1/invoices/{id}/payments", {
        params: { path: { id: invoiceId } },
        body: {
          amount,
          paidAt: paidAtIso,
          notes: paymentNotes.trim() || null,
          reference: paymentReference.trim() || null,
        },
      });

      if (apiError) {
        const message = apiErrorMessage(apiError, "Failed to record payment.");
        toast.error(message);
        return;
      }

      toast.success("Payment recorded");
      setRecordOpen(false);
      setPaymentAmount("");
      setPaymentPaidAt(todayIso());
      setPaymentNotes("");
      setPaymentReference("");
      await Promise.all([fetchPayments(invoiceId), refreshInvoice(invoiceId)]);
    } finally {
      setRecording(false);
    }
  }

  async function handleDeleteInvoice() {
    if (!invoiceId || deletingInvoice) return;
    if (!confirm("Are you sure you want to delete this draft invoice?")) return;
    setDeletingInvoice(true);

    const { error: apiError } = await api.DELETE("/v1/invoices/{id}", {
      params: { path: { id: invoiceId } },
    });

    if (apiError) {
      console.error("Failed to delete invoice:", apiError);
      toast.error(apiErrorMessage(apiError, "Failed to delete invoice."));
      setDeletingInvoice(false);
      return;
    }

    (onDeleted ?? onBack)?.();
  }

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-3xl p-6">
        <LoadingState label="Loading invoice..." />
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="mx-auto w-full max-w-3xl p-6">
        <ErrorState
          message={error ? "The invoice could not be loaded." : "That invoice could not be found."}
          onRetry={onBack ? undefined : () => window.location.reload()}
        />
        {onBack ? (
          <div className="mt-4 flex justify-center">
            <Button variant="outline" size="sm" onClick={onBack}>
              <ArrowLeft className="size-4" /> Back to invoices
            </Button>
          </div>
        ) : null}
      </div>
    );
  }

  const outstanding = Number(invoice.outstanding ?? 0);
  const isOverdue =
    !!invoice.dueDate &&
    (invoice.status === "Issued" || invoice.status === "PartiallyPaid") &&
    new Date(invoice.dueDate) < new Date(new Date().toDateString());

  const isDraft = invoice.status === "Draft";

  // Effective payment terms shown on the invoice: the invoice's own snapshot, else the organisation
  // default, else the platform net-30. Drives the due date the moment the invoice is sent.
  const effectivePaymentTermsDays =
    invoice.paymentTermsDays != null
      ? Number(invoice.paymentTermsDays)
      : arSettings?.defaultPaymentTermsDays != null
        ? Number(arSettings.defaultPaymentTermsDays)
        : 30;
  const paymentTermsLabel =
    effectivePaymentTermsDays === 0 ? "Due on receipt" : `${effectivePaymentTermsDays} days`;

  return (
    <div className="h-full">
      <ResizablePanelGroup orientation="horizontal" className="h-full">
        <ResizablePanel defaultSize={50} minSize={25}>
          <div className="h-full bg-muted p-6">
            <Tabs
              value={previewTab}
              onValueChange={(v) => setPreviewTab(v as "pdf" | "email" | "pay")}
              className="h-full gap-3"
            >
              <TabsList>
                <TabsTrigger value="pdf">Invoice PDF</TabsTrigger>
                {isDraft && arEmailsEnabled ? (
                  <TabsTrigger value="email">Preview Email</TabsTrigger>
                ) : (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <TabsTrigger value="email" disabled>
                          Preview Email
                        </TabsTrigger>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      {!arEmailsEnabled
                        ? "Invoice emails are turned off for this account"
                        : "Preview only available in Draft mode"}
                    </TooltipContent>
                  </Tooltip>
                )}
                {invoice.status === "Credited" ? null : !isDraft ? (
                  <TabsTrigger value="pay">Pay Page</TabsTrigger>
                ) : (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <TabsTrigger value="pay" disabled>
                          Pay Page
                        </TabsTrigger>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      Pay page is available once the invoice is sent
                    </TooltipContent>
                  </Tooltip>
                )}
              </TabsList>

              <TabsContent value="pdf" className="mt-0 flex-1 min-h-0">
                {pdfUrl ? (
                  <PdfFrame
                    src={pdfUrl}
                    title="Invoice PDF"
                    className="w-full h-full rounded-lg border"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-muted-foreground">
                    <Spinner className="size-8" />
                  </div>
                )}
              </TabsContent>

              <TabsContent value="pay" className="mt-0 flex-1 min-h-0">
                {invoice.paymentUrl ? (
                  <iframe
                    src={invoice.paymentUrl}
                    className="w-full h-full rounded-lg border bg-white"
                    title="Pay page"
                  />
                ) : (
                  <div className="flex items-center justify-center h-full text-muted-foreground">
                    Payment link is not available for this invoice.
                  </div>
                )}
              </TabsContent>

              <TabsContent value="email" className="mt-0 flex-1 min-h-0">
                {emailPreviewLoading ? (
                  <div className="flex items-center justify-center h-full text-muted-foreground">
                    <Spinner className="size-8" />
                  </div>
                ) : emailPreviewError ? (
                  <div className="flex items-center justify-center h-full px-6 text-center text-destructive">
                    {emailPreviewError}
                  </div>
                ) : emailPreview ? (
                  <div className="flex flex-col h-full gap-2">
                    <div className="rounded-lg border bg-background px-4 py-2 text-sm">
                      <span className="text-muted-foreground">Subject: </span>
                      <span className="font-medium">{emailPreview.subject}</span>
                    </div>
                    <iframe
                      srcDoc={emailPreview.htmlBody}
                      sandbox=""
                      className="w-full flex-1 rounded-lg border bg-white"
                      title="Email preview"
                    />
                  </div>
                ) : null}
              </TabsContent>
            </Tabs>
          </div>
        </ResizablePanel>

        <ResizableHandle withHandle />

        <ResizablePanel defaultSize={50} minSize={25}>
          <div className="h-full overflow-auto px-6 py-6">
            <div className="max-w-2xl mx-auto space-y-4">
        <div className="flex items-center justify-between gap-2">
          <Button variant="ghost" size="sm" onClick={() => (onBack ? onBack() : window.history.back())}>
            <ArrowLeft className="size-4 mr-1" />
            Back to invoices
          </Button>
          <div className="flex gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={invoice.status !== "Draft"}
                  onClick={() => onEditInvoice(invoiceId)}
                >
                  <Pencil className="size-4" />
                  Edit
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {invoice.status === "Draft"
                ? "Edit this invoice"
                : "Only draft invoices can be edited"}
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={invoice.status !== "Draft" || deletingInvoice}
                  onClick={handleDeleteInvoice}
                >
                  {deletingInvoice ? <Spinner className="size-4" /> : <Trash2 className="size-4" />}
                  Delete
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {invoice.status === "Draft"
                ? "Delete this draft invoice"
                : "Only draft invoices can be deleted"}
            </TooltipContent>
          </Tooltip>
          </div>
        </div>

        <header className="space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  Invoice
                </p>
                {invoice.customer ? (
                  <button
                    type="button"
                    onClick={() => onCustomerClick(invoice.customerId!)}
                    className="block max-w-full truncate text-left text-xl font-bold tracking-tight hover:underline"
                  >
                    {invoice.customer.name}
                  </button>
                ) : (
                  <p className="text-xl font-bold tracking-tight">Invoice</p>
                )}
                {invoice.documentNumber ? (
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <span className="truncate font-mono text-[13px]">{invoice.documentNumber}</span>
                    <CopyButton value={invoice.documentNumber} />
                  </div>
                ) : null}
              </div>
            </div>
            <InvoiceStatusPill status={invoice.status ?? undefined} />
          </div>

          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="text-2xl font-bold tracking-tight tabular-nums">
                {formatCurrency(invoice.totalAmount, invoice.currency)}
              </p>
              <p className="text-sm text-muted-foreground">
                {isDraft ? (
                  `Created ${formatDate(invoice.createdAt ?? invoice.issueDate)}`
                ) : invoice.status === "Paid" || invoice.status === "Credited" ? (
                  "Settled in full"
                ) : invoice.status === "Void" ? (
                  "Voided"
                ) : (
                  <>
                    <span className="tabular-nums">{formatCurrency(outstanding, invoice.currency)}</span>{" "}
                    outstanding
                    {invoice.dueDate ? (
                      <span className={cn(isOverdue && "font-medium text-red-600 dark:text-red-400")}>
                        {" · "}
                        {isOverdue ? "overdue since" : "due"} {formatDate(invoice.dueDate)}
                      </span>
                    ) : null}
                  </>
                )}
              </p>
            </div>
            {invoice.paymentUrl && (
              <div className="flex items-center gap-1">
                <Button asChild size="sm" variant="outline" className="h-8 px-2.5">
                  <a href={invoice.paymentUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="size-3.5" />
                    Payment link
                  </a>
                </Button>
                <CopyButton value={invoice.paymentUrl} />
              </div>
            )}
          </div>

          {invoice.memo && (
            <div className="rounded-lg border bg-card/60 px-3 py-2 text-sm">
              <span className="text-muted-foreground">Memo · </span>
              {invoice.memo}
            </div>
          )}
        </header>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Line items
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {invoice.lineItems?.map((item) => {
              const lineSubTotal = Number(item.subTotal ?? 0);
              const lineTax = Number(item.taxAmount ?? 0);
              // The line's own discount plus its share of any invoice-level one. Both already come
              // out of taxableAmount, which is what the tax was charged on.
              const lineDiscount =
                Number(item.discountAmount ?? 0) + Number(item.allocatedInvoiceDiscount ?? 0);
              // Built from taxableAmount, NOT subTotal + tax: on a discounted line the subtotal is
              // the gross figure, so adding tax to it would overstate the line.
              const lineTotal = Number(item.taxableAmount ?? lineSubTotal) + lineTax;
              const showDescription =
                item.description && item.description !== item.productName;
              return (
                <div key={item.id} className="rounded-lg border bg-muted/30 p-3">
                  <div className="flex items-baseline gap-2 text-sm">
                    <button
                      type="button"
                      onClick={() => onProductClick(item.productId!)}
                      className="font-semibold hover:underline"
                    >
                      {item.productName ?? item.description}
                    </button>
                    {showDescription && (
                      <span className="text-xs text-muted-foreground truncate">
                        - {item.description}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
                    <span>
                      {item.quantity} × {formatCurrency(item.unitPrice, invoice.currency)}
                    </span>
                    {item.taxRateSnapshot && (
                      <span>
                        {/* The name, not the code (VAT_STANDARD): a rate named "IVA 22%" already says its rate. */}
                        {item.taxRateSnapshot.name}
                        {item.taxRateSnapshot.name.includes("%") ? "" : ` · ${item.taxRateSnapshot.rate}%`}
                      </span>
                    )}
                    {!isDraft && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span
                            className="inline-flex items-center text-muted-foreground"
                            aria-label="Tax frozen at send"
                          >
                            <LockIcon className="size-3" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          {item.taxRateSnapshot
                            ? `Frozen at send · ${formatDate(item.taxRateSnapshot.snapshottedAt ?? invoice.issueDate)}`
                            : `Frozen at send · ${formatDate(invoice.issueDate)} - no tax applied`}
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                  <div className="mt-2 flex justify-end gap-6 text-xs">
                    <div className="text-right">
                      <p className="text-muted-foreground">Tax</p>
                      <p className="font-medium tabular-nums">
                        {formatCurrency(lineTax, invoice.currency)}
                      </p>
                    </div>
                    {lineDiscount > 0 && (
                      <div className="text-right">
                        <p className="text-muted-foreground">Discount</p>
                        <p className="font-medium tabular-nums">
                          -{formatCurrency(lineDiscount, invoice.currency)}
                        </p>
                      </div>
                    )}
                    <div className="text-right">
                      <p className="text-muted-foreground">Subtotal</p>
                      <p className="font-medium tabular-nums">
                        {formatCurrency(lineSubTotal, invoice.currency)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-muted-foreground">Total</p>
                      <p className="font-semibold tabular-nums">
                        {formatCurrency(lineTotal, invoice.currency)}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Subtotal</span>
              <span>{formatCurrency(invoice.subTotal, invoice.currency)}</span>
            </div>
            {/* One row for both discount levels: the payer cares about the reduction. Tax below is charged on the discounted amount. */}
            {Number(invoice.discountTotal ?? 0) > 0 && (
              <div className="flex justify-between text-sm">
                <span>Discount</span>
                <span className="tabular-nums">
                  -{formatCurrency(invoice.discountTotal, invoice.currency)}
                </span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span>Tax</span>
              <span>{formatCurrency(invoice.taxAmount, invoice.currency)}</span>
            </div>
            <Separator className="my-2" />
            <div className="flex justify-between font-bold">
              <span>Total</span>
              <span>{formatCurrency(invoice.totalAmount, invoice.currency)}</span>
            </div>
            {invoice.earlyPaymentDiscount ? (
              <EarlyPaymentDiscountRow
                discount={invoice.earlyPaymentDiscount}
                currency={invoice.currency}
                creditNote={creditNotes.find((cn) => cn.reasonCode === "EarlyPaymentDiscount" && cn.status === "Issued")}
                onCreditNoteClick={onCreditNoteClick}
              />
            ) : null}
            {Number(invoice.amountCredited ?? 0) > 0 && (
              <>
                <div className="flex justify-between text-sm text-emerald-600 dark:text-emerald-400">
                  <span>Credit applied</span>
                  <span className="tabular-nums">
                    −{formatCurrency(invoice.amountCredited, invoice.currency)}
                  </span>
                </div>
                <Separator className="my-2" />
                <div className="flex justify-between font-bold">
                  <span>Balance due</span>
                  <span className="tabular-nums">
                    {formatCurrency(invoice.outstanding, invoice.currency)}
                  </span>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card id="payments" className="scroll-mt-4">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">
              Payments
            </CardTitle>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Dialog open={recordOpen} onOpenChange={setRecordOpen}>
                    <DialogTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!PAYMENT_RECORDABLE_STATUSES.has(invoice.status ?? "")}
                      >
                        <Plus className="size-4" />
                        Record
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Record a payment</DialogTitle>
                        <DialogDescription>
                          Log a payment received off-platform (bank transfer, cash, cheque).
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-2">
                        <div className="space-y-2">
                          <Label htmlFor="payment-amount">Amount ({invoice.currency})</Label>
                          <Input
                            id="payment-amount"
                            type="number"
                            inputMode="decimal"
                            step="0.01"
                            min="0"
                            placeholder="0.00"
                            value={paymentAmount}
                            onChange={(e) => setPaymentAmount(e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Paid on</Label>
                          <DatePicker
                            value={paymentPaidAt}
                            onChange={setPaymentPaidAt}
                            placeholder="Select a date"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="payment-reference">Reference</Label>
                          <Input
                            id="payment-reference"
                            maxLength={100}
                            placeholder="e.g. cheque number or bank reference"
                            value={paymentReference}
                            onChange={(e) => setPaymentReference(e.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="payment-notes">Notes</Label>
                          <Textarea
                            id="payment-notes"
                            rows={3}
                            placeholder="e.g. Paid at the counter"
                            value={paymentNotes}
                            onChange={(e) => setPaymentNotes(e.target.value)}
                          />
                        </div>
                      </div>
                      <DialogFooter>
                        <Button
                          variant="outline"
                          onClick={() => setRecordOpen(false)}
                          disabled={recording}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleRecordPayment}
                          disabled={recording}
                        >
                          {recording ? <Spinner className="size-4" /> : null}
                          {recording ? "Recording..." : "Record Payment"}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {PAYMENT_RECORDABLE_STATUSES.has(invoice.status ?? "")
                  ? "Record a manual payment"
                  : "Payments can only be recorded on sent or partially paid invoices"}
              </TooltipContent>
            </Tooltip>
          </CardHeader>
          <CardContent>
            {paymentsLoading ? (
              <div className="flex items-center justify-center py-6">
                <Spinner className="size-5 text-muted-foreground" />
              </div>
            ) : payments.length === 0 ? (
              <p className="py-2 text-sm text-muted-foreground">
                {isDraft
                  ? "Payments appear here once the invoice is sent and the customer pays."
                  : "No payments recorded yet."}
              </p>
            ) : (
              <TableShell className="rounded-lg bg-transparent backdrop-blur-none">
              <Table>
                <TableHeader>
                  <HeaderRow>
                    <TableHead className={TH}>Date</TableHead>
                    <TableHead className={TH}>Method</TableHead>
                    <TableHead className={TH}>Paid by</TableHead>
                    <TableHead className={TH}>Reference</TableHead>
                    <TableHead className={TH}>Status</TableHead>
                    <TableHead className={cn(TH, "text-right")}>Amount</TableHead>
                    {accountingEnabled ? <TableHead className={TH}>Accounting</TableHead> : null}
                    <TableHead className="w-10" />
                  </HeaderRow>
                </TableHeader>
                <TableBody>
                  {payments.map((p) => {
                    const date = p.succeededAt ?? p.createdAt;
                    return (
                      <TableRow key={p.id}>
                        <TableCell>{formatDate(date)}</TableCell>
                        <TableCell>
                          <PaymentMethodCell payment={p} />
                        </TableCell>
                        <TableCell>
                          <PaymentPayerCell payment={p} />
                        </TableCell>
                        <TableCell className="text-sm">{p.reference ?? <span className="text-muted-foreground">-</span>}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {p.status}
                        </TableCell>
                        <TableCell className="text-right font-medium tabular-nums">
                          {formatCurrency(p.amount, p.currency)}
                        </TableCell>
                        {accountingEnabled ? (
                          <TableCell>
                            <AccountingSyncBadge sync={p.accountingSync} />
                          </TableCell>
                        ) : null}
                        <TableCell>
                          {p.receiptUrl ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <a
                                  href={p.receiptUrl}
                                  target="_blank"
                                  rel="noreferrer noopener"
                                  className="text-muted-foreground hover:text-foreground inline-flex"
                                  aria-label="View receipt"
                                >
                                  <Receipt className="size-4" />
                                </a>
                              </TooltipTrigger>
                              <TooltipContent>View receipt</TooltipContent>
                            </Tooltip>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              </TableShell>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base">
              Credit notes
            </CardTitle>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Dialog
                    open={cnDialogOpen}
                    onOpenChange={(open) => {
                      setCnDialogOpen(open);
                      if (!open) resetCnForm();
                    }}
                  >
                    <DialogTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={!CREDIT_NOTE_CREATABLE_STATUSES.has(invoice.status ?? "")}
                      >
                        <Plus className="size-4" />
                        New credit note
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>New credit note</DialogTitle>
                        <DialogDescription>
                          Raise a credit note against this invoice. It starts as a draft;
                          issuing it applies the credit and reduces the balance due.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-2">
                        {invoice.lineItems && invoice.lineItems.length > 0 && (
                          <div className="space-y-2">
                            <Label htmlFor="cn-line-item">Line item</Label>
                            <Select
                              value={cnLineItemId || "__whole__"}
                              onValueChange={(v) => {
                                const id = v === "__whole__" ? "" : v;
                                setCnLineItemId(id);
                                // Clear any whole-invoice rate pick — a line credit inherits the
                                // line's frozen rate, so a leftover selection must not be sent.
                                setCnTaxRateId("");
                                const line = invoice.lineItems?.find((li) => li.id === id);
                                if (line) {
                                  // From taxableAmount, so a discounted line pre-fills what was
                                  // actually billed rather than its pre-discount gross.
                                  const billed =
                                    Number(line.taxableAmount ?? line.subTotal ?? 0) +
                                    Number(line.taxAmount ?? 0);
                                  setCnAmount(billed.toFixed(2));
                                }
                              }}
                            >
                              <SelectTrigger id="cn-line-item" className="w-full">
                                <SelectValue placeholder="Whole invoice" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__whole__">Whole invoice</SelectItem>
                                {invoice.lineItems.map((li) => (
                                  <SelectItem key={li.id} value={li.id!}>
                                    {li.description}
                                    {li.taxRateSnapshot?.rate != null
                                      ? ` · ${Number(li.taxRateSnapshot.rate)}% tax`
                                      : ""}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        {/* Inherited for a line-item credit, selectable for a whole-invoice credit. */}
                        <div className="space-y-2">
                          <Label htmlFor="cn-tax-rate">Tax rate</Label>
                          {cnLineItemId ? (
                            (() => {
                              const snap = invoice.lineItems?.find(
                                (li) => li.id === cnLineItemId,
                              )?.taxRateSnapshot;
                              return (
                                <p className="rounded-md border bg-muted/40 px-3 py-2 text-sm">
                                  {snap ? `${snap.code} · ${Number(snap.rate)}%` : "No tax"}{" "}
                                  <span className="text-xs text-muted-foreground">
                                    - inherited from the line item
                                  </span>
                                </p>
                              );
                            })()
                          ) : (
                            <Select
                              // Mixed rates offer no default, so "" lets the placeholder show.
                              value={cnTaxRateId || (cnRatesDiffer ? "" : "__none__")}
                              onValueChange={(v) => setCnTaxRateId(v === "__none__" ? "" : v)}
                            >
                              <SelectTrigger id="cn-tax-rate" className="w-full">
                                <SelectValue placeholder="Choose a rate" />
                              </SelectTrigger>
                              <SelectContent>
                                {cnRatesDiffer ? null : (
                                  <SelectItem value="__none__">
                                    {cnInvoiceRate
                                      ? `Invoice's rate · ${cnInvoiceRate.code} · ${Number(cnInvoiceRate.rate)}%`
                                      : "No tax (zero-rated)"}
                                  </SelectItem>
                                )}
                                {taxRates.map((tr) => (
                                  <SelectItem key={tr.id} value={tr.id!}>
                                    {tr.code} · {tr.name} · {Number(tr.rate)}%
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="cn-amount">Amount ({invoice.currency})</Label>
                          <Input
                            id="cn-amount"
                            type="number"
                            inputMode="decimal"
                            step="0.01"
                            min="0"
                            placeholder="0.00"
                            value={cnAmount}
                            onChange={(e) => setCnAmount(e.target.value)}
                          />
                          <p className="text-xs text-muted-foreground">
                            Outstanding balance:{" "}
                            {formatCurrency(invoice.outstanding, invoice.currency)}
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="cn-reason">Reason</Label>
                          <Textarea
                            id="cn-reason"
                            rows={3}
                            placeholder="e.g. Returned goods, overcharge correction, goodwill"
                            value={cnReason}
                            onChange={(e) => setCnReason(e.target.value)}
                          />
                        </div>
                      </div>
                      <DialogFooter>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setCnDialogOpen(false);
                            resetCnForm();
                          }}
                          disabled={cnCreating}
                        >
                          Cancel
                        </Button>
                        <Button onClick={handleCreateCreditNote} disabled={cnCreating || cnNeedsRate}>
                          {cnCreating ? <Spinner className="size-4" /> : null}
                          {cnCreating ? "Creating..." : "Create credit note"}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {CREDIT_NOTE_CREATABLE_STATUSES.has(invoice.status ?? "")
                  ? "Raise a credit note against this invoice"
                  : "Credit notes can only be raised on sent or partially paid invoices"}
              </TooltipContent>
            </Tooltip>
          </CardHeader>
          <CardContent>
            {creditNotesLoading ? (
              <div className="flex items-center justify-center py-6">
                <Spinner className="size-5 text-muted-foreground" />
              </div>
            ) : creditNotes.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">
                No credit notes yet.
              </p>
            ) : (
              <div className="space-y-3">
                {creditNotes.map((cn) => {
                  const isDraftCn = cn.status === "Draft";
                  // "Issuing" is an in-flight two-phase claim: show it in progress, not as a green applied credit.
                  const isIssued = cn.status === "Issued";
                  return (
                    <div key={cn.id} className="flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {onCreditNoteClick && cn.id ? (
                            <button
                              type="button"
                              className="text-sm font-medium font-mono truncate text-accent-blue hover:underline"
                              onClick={() => onCreditNoteClick(cn.id!)}
                            >
                              {cn.documentNumber}
                            </button>
                          ) : (
                            <span className="text-sm font-medium font-mono truncate">
                              {cn.documentNumber}
                            </span>
                          )}
                          {isDraftCn ? (
                            <Badge variant="outline">Draft</Badge>
                          ) : isIssued ? (
                            <Badge
                              variant="outline"
                              className="border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                            >
                              Issued
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-muted-foreground">
                              Issuing
                            </Badge>
                          )}
                          {isIssued && invoiceId && (
                            <CreditNoteEInvoiceStatus
                              invoiceId={invoiceId}
                              creditNoteId={cn.id!}
                              issuedAt={cn.issuedAt}
                              enabled={eInvoicingEnabled ?? true}
                              canRetry={canSendChase}
                              registered={eInvoicingRegistered}
                            />
                          )}
                          {isIssued && accountingEnabled ? <AccountingSyncBadge sync={cn.accountingSync} /> : null}
                        </div>
                        {cn.reason && (
                          <p className="text-xs text-muted-foreground truncate">
                            {cn.reason}
                          </p>
                        )}
                      </div>
                      <span
                        className={`text-sm font-medium tabular-nums ${
                          isIssued
                            ? "text-emerald-600 dark:text-emerald-400"
                            : "text-muted-foreground"
                        }`}
                      >
                        {isIssued
                          ? `−${formatCurrency(cn.amount, cn.currency)}`
                          : formatCurrency(cn.amount, cn.currency)}
                      </span>
                      {isDraftCn && (
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            onClick={() => handleIssueCreditNote(cn.id!)}
                            disabled={issuingCnId === cn.id || deletingCnId === cn.id}
                          >
                            {issuingCnId === cn.id ? (
                              <Spinner className="size-4" />
                            ) : null}
                            Issue
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            aria-label="Delete draft credit note"
                            onClick={() => handleDeleteCreditNote(cn.id!)}
                            disabled={issuingCnId === cn.id || deletingCnId === cn.id}
                          >
                            {deletingCnId === cn.id ? (
                              <Spinner className="size-4" />
                            ) : (
                              <Trash2 className="size-4" />
                            )}
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {!bankDetailsConfigured && (
          <Card className="border-amber-500/50 bg-amber-50 dark:bg-amber-950/20">
            <CardContent className="pt-4 pb-4 flex items-center justify-between gap-3">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                Bank details have not been configured.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="shrink-0 border-amber-500/50 text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-950/40"
                onClick={() => setSettingsOpen(true)}
              >
                Configure
              </Button>
            </CardContent>
          </Card>
        )}

        <OrganisationSettingsDialog
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
          scrollToBankDetails
          onSaved={refreshBankDetails}
        />

        {accountingEnabled && invoiceId ? (
          <InvoiceAccountingSyncCard
            invoiceId={invoiceId}
            sync={invoice.accountingSync}
            status={invoice.status}
            onRefresh={async () => {
              await Promise.all([refreshInvoice(invoiceId), fetchPayments(invoiceId), fetchCreditNotes(invoiceId)]);
            }}
          />
        ) : null}

        {booksCard}

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Key dates</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-[8rem_minmax(0,1fr)] gap-x-4 gap-y-2.5 text-sm">
              <dt className="text-muted-foreground">Created</dt>
              <dd>{formatDateTime(invoice.createdAt, "long")}</dd>
              <dt className="text-muted-foreground">Updated</dt>
              <dd>{formatDateTime(invoice.updatedAt, "long")}</dd>
              {/* A draft has no issue or due date yet; the API sends its placeholder (year 1), not null. */}
              <dt className="text-muted-foreground">Issued</dt>
              <dd className={cn(invoice.status === "Draft" && "text-muted-foreground")}>
                {invoice.status === "Draft" ? "Not issued yet" : formatDateTime(invoice.issueDate, "long")}
              </dd>
              <dt className="text-muted-foreground">Due</dt>
              <dd className={cn(isOverdue && "font-medium text-red-600 dark:text-red-400", invoice.status === "Draft" && "text-muted-foreground")}>
                {invoice.status === "Draft" ? "Set when issued" : formatDate(invoice.dueDate, "long")}
              </dd>
              <dt className="flex items-center gap-1 text-muted-foreground">
                Payment terms
                <InfoHint label="More about payment terms">
                  Sets the due date when the invoice is sent. The due date is this many days after the
                  issue date.
                </InfoHint>
              </dt>
              <dd>{paymentTermsLabel}</dd>
            </dl>
          </CardContent>
        </Card>

        {/* Hidden when the log is empty rather than showing an empty card. */}
        {history.length > 0 ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">History</CardTitle>
            </CardHeader>
            <CardContent>
              <Timeline entries={history.map(historyEntry)} />
            </CardContent>
          </Card>
        ) : null}

        {whatsAppEnabled && invoiceId ? (
          <InvoiceWhatsAppMessages invoiceId={invoiceId} refreshKey={`${invoice.status}|${invoice.updatedAt}`} />
        ) : null}

        <div className="flex flex-col gap-2 pt-2">
          {invoice.deliveries?.some((d) => d.status === "NotSentDemo") ? (
            <p className="flex items-center gap-2 rounded-md border border-dashed px-3 py-2 text-sm text-muted-foreground">
              <Mail className="size-4 shrink-0" />
              Email delivery: Not sent - demo
            </p>
          ) : null}
          <Dialog open={sendModalOpen} onOpenChange={setSendModalOpen}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="w-full">
                  <DialogTrigger asChild>
                    <Button
                      className="w-full text-base font-semibold shadow-md shadow-primary/25 transition-all duration-200 hover:bg-primary/90 hover:shadow-lg hover:shadow-primary/30 hover:-translate-y-0.5 active:translate-y-0 active:shadow-sm"
                      size="lg"
                      disabled={!sendable || !canSendChase}
                    >
                      <Send className="size-5" />
                      Send Invoice
                      {whatsAppEnabled ? <WhatsAppIcon className="size-4" mono /> : null}
                    </Button>
                  </DialogTrigger>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {sendDemoReason
                  ? sendDemoReason
                  : !canSendChase
                  ? "Only owners and admins can send invoices"
                  : !sendable
                    ? "Paid, credited and void invoices have nothing to send"
                    : alreadyDelivered
                      ? "Send the invoice to the customer again. An e-invoice is never sent over the network twice."
                      : whatsAppEnabled
                        ? "Send this invoice to the customer by email, and on WhatsApp if they take it"
                        : "Send this invoice to the customer"}
              </TooltipContent>
            </Tooltip>
            <DialogContent className="flex max-h-[90vh] flex-col gap-4 overflow-hidden sm:max-w-5xl">
              <DialogHeader>
                <DialogTitle>Send Invoice</DialogTitle>
                <DialogDescription>
                  {sendPlan?.eInvoice && !sendPlan.eInvoice.status
                    ? `This invoice goes over ${networkLabel(sendPlan.eInvoice.network)} first.` +
                      (!sendPlan.email
                        ? ""
                        : sendPlan.email.timing === "Now"
                          ? " Its PDF copy is emailed straight away."
                          : " Its PDF copy is emailed once delivery is confirmed.")
                    : emailCopyPlanned
                      ? "Review and tweak the email below before sending. The invoice PDF and payment link are attached automatically."
                      : "Check where this invoice goes before sending it."}
                </DialogDescription>
              </DialogHeader>
              <SendPlanSummary
                plan={sendPlan}
                loading={sendPlanLoading}
                error={sendPlanError}
                onRecheck={() => void loadSendPlan()}
                onFix={(fix) => void handleFix(fix)}
                canFix={(fix) => fix !== "Organisation" || !!onEInvoicingSettingsClick}
                emailSkipped={emailChoice === "Skip"}
                notRegistered={eInvoicingEnabled && eInvoicingRegistered === false}
              />
              <SendChannelChoices
                plan={sendPlan}
                eInvoice={eInvoiceChoice}
                email={emailChoice}
                onEInvoiceChange={setEInvoiceChoice}
                onEmailChange={setEmailChoice}
              />
              {emailCopyPlanned ? (
              <div className="flex min-h-0 flex-1 flex-col gap-2">
              {sendPlan?.eInvoice ? (
                <p className="text-xs font-bold tracking-wider text-muted-foreground uppercase">PDF copy by email</p>
              ) : null}
              <div className="grid min-h-0 flex-1 gap-6 md:grid-cols-2">
                {/* Re-renders on tone change and on field edits (debounced), so it shows the exact email to be sent. */}
                <div className="relative rounded-lg border bg-white overflow-hidden">
                  {modalPreviewHtml ? (
                    <iframe
                      srcDoc={modalPreviewHtml}
                      sandbox=""
                      className="w-full h-full"
                      title="Email preview"
                    />
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <Spinner className="size-6" />
                    </div>
                  )}
                  {modalPreviewLoading && modalPreviewHtml ? (
                    <div className="absolute top-2 right-2 rounded-full bg-background/90 p-1.5 shadow-sm">
                      <Spinner className="size-4" />
                    </div>
                  ) : null}
                </div>
                <div className="space-y-4 overflow-y-auto">
                  {modalPreviewError ? (
                    <div className="rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive">
                      {modalPreviewError}
                    </div>
                  ) : null}
                  <div className="space-y-2">
                    <Label htmlFor="modal-tone">Tone</Label>
                    <Select
                      value={modalTone}
                      onValueChange={(v) => setModalTone(v as InvoiceTone)}
                      disabled={modalPreviewLoading || sending}
                    >
                      <SelectTrigger id="modal-tone">
                        {/* The bare label, so the description only appears in the open dropdown. */}
                        <SelectValue>
                          {TONE_OPTIONS.find((o) => o.value === modalTone)?.label}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {TONE_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            <div className="flex flex-col">
                              <span>{opt.label}</span>
                              <span className="text-xs text-muted-foreground">{opt.description}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Reminders</Label>
                    <ReminderOffsetMultiSelect
                      value={modalReminders}
                      onChange={(next) => {
                        setModalReminders(next);
                        // Dirty only if it differs from the org default: clicking back to the default sends no override.
                        const orgDefault = arSettings?.defaultReminderOffsets ?? [];
                        setModalRemindersDirty(!reminderOffsetsEqual(next, orgDefault));
                      }}
                      disabled={sending}
                      dirty={modalRemindersDirty}
                    />
                    <p className="text-xs text-muted-foreground">
                      Auto-scheduled reminders for this invoice, relative to its due date. Leaving the selection on your organisation default means future changes to the default won't retroactively affect this send.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="modal-subject">Subject</Label>
                    <Input
                      id="modal-subject"
                      value={modalSubject}
                      onChange={(e) => {
                        setModalSubject(e.target.value);
                        setModalSubjectDirty(true);
                      }}
                      disabled={sending}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="modal-cc">CC</Label>
                    <Input
                      id="modal-cc"
                      value={modalCc}
                      placeholder="accounts@example.com, bookkeeper@example.com"
                      onChange={(e) => {
                        setModalCc(e.target.value);
                        setModalCcDirty(true);
                      }}
                      disabled={sending}
                    />
                    <p className="text-xs text-muted-foreground">
                      Comma-separated. Prefilled from the invoice / organisation default; edits apply to this send and its reminders.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="modal-reply-to">Reply-To</Label>
                    <Input
                      id="modal-reply-to"
                      type="email"
                      value={modalReplyTo}
                      placeholder="billing@example.com"
                      onChange={(e) => {
                        setModalReplyTo(e.target.value);
                        setModalReplyToDirty(true);
                      }}
                      disabled={sending}
                    />
                    <p className="text-xs text-muted-foreground">
                      Where customer replies go for this email. Defaults to your organisation's Reply-To.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="modal-greeting">Greeting</Label>
                    <Input
                      id="modal-greeting"
                      value={modalGreetingLine}
                      onChange={(e) => {
                        setModalGreetingLine(e.target.value);
                        setModalGreetingLineDirty(true);
                      }}
                      disabled={sending}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="modal-body">Opening message</Label>
                    <Textarea
                      id="modal-body"
                      value={modalOpeningBody}
                      onChange={(e) => {
                        setModalOpeningBody(e.target.value);
                        setModalOpeningBodyDirty(true);
                      }}
                      rows={6}
                      className="font-mono text-xs"
                      disabled={sending}
                    />
                    <p className="text-xs text-muted-foreground">
                      Markdown supported. The invoice total, due date, and payment options appear below this in the email.
                    </p>
                  </div>
                </div>
              </div>
              </div>
              ) : !arEmailsEnabled ? (
                <div className="flex min-h-[200px] flex-col items-center justify-center gap-3 rounded-lg border border-dashed bg-muted/30 p-8 text-center">
                  <Mail className="size-8 text-muted-foreground" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Invoice emails are turned off</p>
                    <p className="text-sm text-muted-foreground">
                      This account doesn't send invoices by email. The invoice will be marked as
                      sent and its PDF and payment link stay available to share manually - no email
                      goes to the customer.
                    </p>
                  </div>
                </div>
              ) : null}
              <DialogFooter>
                <Button
                  disabled={
                    sending
                    || sendPlanLoading
                    || !sendPlan?.canSend
                    || (emailCopyPlanned && (modalPreviewLoading || !!modalPreviewError))
                  }
                  onClick={handleSendInvoice}
                >
                  {sending ? <Spinner className="size-4" /> : <Send className="size-4" />}
                  {sending
                    ? "Sending..."
                    : sendPlan?.eInvoice && !sendPlan.eInvoice.status
                      ? `Send via ${networkLabel(sendPlan.eInvoice.network)}`
                      : "Send Invoice"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          {invoice.status === "Draft" ? (
            <Dialog open={issueConfirmOpen} onOpenChange={setIssueConfirmOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="lg" className="w-full" disabled={!canIssue}>
                  <FileCheck2 className="size-5" />
                  Issue Invoice
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Issue this invoice?</DialogTitle>
                  <DialogDescription>
                    It gets its number and date and can no longer be edited. Nothing is sent to the customer; send it
                    when you're ready, or share its PDF and payment link yourself.
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIssueConfirmOpen(false)} disabled={issuing}>
                    Cancel
                  </Button>
                  <Button onClick={handleIssueOnly} disabled={issuing}>
                    {issuing ? <Spinner className="size-4" /> : <FileCheck2 className="size-4" />}
                    Issue Invoice
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          ) : null}
          <EditCustomerDialog
            customer={fixCustomer}
            open={fixCustomerOpen}
            onOpenChange={setFixCustomerOpen}
            onUpdated={() => {
              setFixCustomerOpen(false);
              void loadSendPlan();
            }}
          />
          {/* Sending itself is the Send button above; this tracks the network delivery. Renders nothing without the module. */}
          {invoiceId && (
            <EInvoiceDelivery
              invoiceId={invoiceId}
              invoiceStatus={invoice.status}
              canSend={canSendChase}
              enabled={eInvoicingEnabled}
              registered={eInvoicingRegistered}
              onEInvoicingSettingsClick={onEInvoicingSettingsClick}
              deliverAsEInvoice={invoice.deliverAsEInvoice}
              pdfCopyTo={invoice.pdfCopyTo}
              hubBaseUrl={hubBaseUrl}
            />
          )}
          <Button
            className="w-full text-base font-semibold border-2 border-primary/40 bg-transparent text-primary shadow-sm transition-all duration-200 hover:bg-primary/10 hover:border-primary/60 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 active:shadow-sm"
            size="lg"
            variant="outline"
            onClick={handleDownloadPdf}
          >
            <Download className="size-5" />
            Download PDF
          </Button>
        </div>
            </div>
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  );
}

function PaymentMethodCell({ payment }: { payment: InvoicePaymentResponse }) {
  const pm = payment.paymentMethod;
  if (pm?.type === "card" && pm.brand && pm.last4) {
    const brand = pm.brand.charAt(0).toUpperCase() + pm.brand.slice(1);
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm">
          {brand} •••• {pm.last4}
        </span>
        {pm.wallet ? <WalletBadge wallet={pm.wallet} /> : null}
      </div>
    );
  }
  if (pm?.type) {
    return <Badge variant="outline">{pm.type}</Badge>;
  }
  return <Badge variant="outline">{payment.provider ?? "Manual"}</Badge>;
}

function WalletBadge({ wallet }: { wallet: string }) {
  const label =
    wallet === "apple_pay" ? "Apple Pay" :
    wallet === "google_pay" ? "Google Pay" :
    wallet;
  return (
    <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
      {label}
    </Badge>
  );
}

function PaymentPayerCell({ payment }: { payment: InvoicePaymentResponse }) {
  const name = payment.billing?.name;
  const email = payment.billing?.email;
  if (!name && !email) {
    return <span className="text-muted-foreground text-xs">-</span>;
  }
  return (
    <div className="flex flex-col">
      {name ? <span className="text-sm">{name}</span> : null}
      {email ? (
        <span className="text-xs text-muted-foreground">{email}</span>
      ) : null}
    </div>
  );
}

/** Active until its date, Expired after, or Taken once paid at the discount (linking the closing credit note). */
function EarlyPaymentDiscountRow({
  discount,
  currency,
  creditNote,
  onCreditNoteClick,
}: {
  discount: components["schemas"]["EarlyPaymentDiscountResponse"];
  currency: string;
  creditNote: CreditNoteResponse | undefined;
  onCreditNoteClick?: (creditNoteId: string) => void;
}) {
  const expiresOn = discount.expiresOn ?? null;
  const expired = expiresOn ? (daysFromToday(expiresOn) ?? 0) < 0 : false;
  const status = creditNote ? "Taken" : expired ? "Expired" : "Active";
  const tone =
    status === "Taken"
      ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
      : status === "Active"
        ? "border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-300"
        : "text-muted-foreground";

  return (
    <div className="space-y-1 rounded-md bg-muted/40 p-2.5 text-sm" data-testid="early-payment-discount">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-2">
          Early payment discount
          <Badge variant="outline" className={tone}>
            {status}
          </Badge>
        </span>
        <span className="tabular-nums">-{formatCurrency(discount.amount, currency)}</span>
      </div>
      <p className="text-xs text-muted-foreground">
        {formatCurrency(discount.discountedTotal, currency)} if paid by{" "}
        {expiresOn ? formatCalendarDate(expiresOn) : "-"}
        {creditNote?.id ? (
          <>
            {" - credited by "}
            {onCreditNoteClick ? (
              <button
                type="button"
                className="font-mono underline underline-offset-2 hover:text-foreground"
                onClick={() => onCreditNoteClick(creditNote.id!)}
              >
                {creditNote.documentNumber}
              </button>
            ) : (
              <span className="font-mono">{creditNote.documentNumber}</span>
            )}
          </>
        ) : null}
      </p>
    </div>
  );
}
